import { PencilIcon } from 'lucide-react';

import { useId, useRef } from 'react';

import { type ItemAvailability, formatNameList } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  formatAmountForCurrency,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

import { SelectCircle } from '@/components/common/SelectCircle';
import { SharesStepper } from '@/components/common/SharesStepper';

export interface LineItemRowProps {
  item: ExpenseLineItem;
  /** How the row reads for the selected person; `takenBy` in the order the names should read. */
  availability: ItemAvailability;
  nameOf: (userId: string) => string;
  currency: string;
  /** Whose picks the row shows, when not the viewer's own. */
  selectedName?: string;
  /** Nobody picked this row but something else on the expense is picked: say who pays for it. */
  unpickedHint?: boolean;
  /** Replaces the availability line, for a page with its own wording (the bill's link). */
  caption?: string;
  /** No circle, stepper or pencil: the person cannot pick here (a summary, not in the split). */
  readOnly?: boolean;
  /** Offline or saving: the circle and stepper stay visible but do not respond. */
  disabled?: boolean;
  /** The circle or the row: takes one unit, or releases all of the person's. */
  onToggle: () => void;
  /** The stepper: 0 releases the row. */
  onUnits: (units: number) => void;
  /** Without it the row has no pencil (the detail's claim list). */
  onEdit?: () => void;
}

/** The name as a title reads it: the placeholder for an unread name, the quantity in front. */
export function useLineItemLabel() {
  const { t } = useTranslation('expenses');
  return (item: ExpenseLineItem) => {
    const name = item.name.trim() || t('lineItems.unnamed');
    return item.quantity > 1
      ? t('lineItems.itemWithQuantity', { quantity: item.quantity, name })
      : name;
  };
}

export function LineItemRow({
  item,
  availability,
  nameOf,
  currency,
  selectedName,
  unpickedHint = false,
  caption,
  readOnly = false,
  disabled: frozen = false,
  onToggle,
  onUnits,
  onEdit,
}: LineItemRowProps) {
  const { t, i18n } = useTranslation('expenses');
  const describedById = useId();
  const circleRef = useRef<HTMLButtonElement>(null);

  const unnamed = item.name.trim() === '';
  const name = unnamed ? t('lineItems.unnamed') : item.name;
  const amount = formatAmountForCurrency(item.amount, currency);
  // Rounded the way the item editor shows it, so both read the same price.
  const eachUnit =
    item.quantity > 1 && item.amount > 0
      ? t('lineItems.eachUnit', {
          amount: formatAmountForCurrency(
            roundToCurrencyPrecision(item.amount / item.quantity, currency),
            currency
          ),
        })
      : null;
  const { quantity } = availability;
  const mine = availability.kind === 'mine' ? availability.mine : 0;
  const taken = availability.kind === 'taken';
  const inert = taken || frozen;
  const stepper =
    availability.kind === 'mine' && availability.mine + availability.left > 1;

  const line = (() => {
    if (caption !== undefined) return caption;
    switch (availability.kind) {
      case 'mine': {
        const mineOf = selectedName
          ? t('lineItems.theirsOf', { name: selectedName, mine, quantity })
          : t('lineItems.mineOf', { mine, quantity });
        return availability.left > 0
          ? `${mineOf} – ${t('lineItems.left', { left: availability.left })}`
          : mineOf;
      }
      case 'partial':
        return t('lineItems.leftOf', { left: availability.left, quantity });
      case 'taken':
        return t('lineItems.takenBy', {
          names: formatNameList(
            availability.takenBy.map(nameOf),
            i18n.language
          ),
        });
      default:
        return unpickedHint ? t('lineItems.unpicked') : null;
    }
  })();
  // The units sentence already says "mine of quantity".
  const lineForLabel =
    availability.kind === 'mine'
      ? availability.left > 0
        ? t('lineItems.left', { left: availability.left })
        : null
      : line;
  const units = selectedName
    ? t('lineItems.unitsA11yName', {
        item: name,
        amount,
        name: selectedName,
        mine,
        quantity,
      })
    : t('lineItems.unitsA11y', { item: name, amount, mine, quantity });
  const rowLabel = [units, eachUnit, lineForLabel].filter(Boolean).join(', ');

  const toggle = () => {
    if (!readOnly && !inert) onToggle();
  };

  return (
    <li className="flex items-start gap-1 border-b border-gray-100 last:border-b-0 dark:border-gray-700/60">
      {readOnly ? null : (
        <>
          {/* A described-by target may be hidden; it gives the circle the whole row. */}
          <span id={describedById} hidden>
            {rowLabel}
          </span>
          <button
            ref={circleRef}
            type="button"
            aria-pressed={mine > 0}
            aria-disabled={inert || undefined}
            aria-label={t(
              mine > 0 ? 'lineItems.releaseA11y' : 'lineItems.takeA11y',
              { item: name }
            )}
            aria-describedby={describedById}
            onClick={toggle}
            className={`mt-0.5 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              inert ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <SelectCircle checked={mine > 0} disabled={inert} />
          </button>
        </>
      )}
      <div className={`min-w-0 flex-1 py-3 ${readOnly ? 'px-2' : ''}`}>
        {/* Clicking anywhere on the text toggles too; the circle is the control. */}
        <div
          onClick={toggle}
          className={`flex flex-wrap items-baseline gap-x-3 gap-y-0.5 ${
            readOnly || inert ? '' : 'cursor-pointer'
          } ${inert && !readOnly ? 'opacity-60' : ''}`}
        >
          <span
            data-testid="line-item-name"
            className={`min-w-[45%] flex-1 break-words text-base line-clamp-3 ${
              unnamed
                ? 'italic text-gray-500 dark:text-gray-400'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {name}
          </span>
          <span
            data-testid="line-item-value"
            className="ml-auto flex max-w-full flex-wrap items-baseline justify-end gap-x-3"
          >
            {item.quantity > 1 ? (
              <span
                data-testid="line-item-quantity"
                className="text-sm tabular-nums text-gray-500 dark:text-gray-400"
              >
                {`\u00d7${item.quantity}`}
              </span>
            ) : null}
            <span className="break-words text-right text-base font-medium tabular-nums text-gray-900 dark:text-gray-50">
              {amount}
            </span>
          </span>
          {eachUnit ? (
            <span
              data-testid="line-item-unit-price"
              className="basis-full text-sm tabular-nums text-gray-500 dark:text-gray-400"
            >
              {eachUnit}
            </span>
          ) : null}
          {line ? (
            <span
              data-testid="line-item-availability"
              className="basis-full text-sm text-gray-500 dark:text-gray-400"
            >
              {line}
            </span>
          ) : null}
        </div>
        {stepper && !readOnly ? (
          <div className="-ml-2 mt-1">
            <SharesStepper
              value={mine}
              min={0}
              max={availability.mine + availability.left}
              disabled={frozen}
              labels={{
                decrease: t('lineItems.unitLessA11y', { item: name }),
                increase: t('lineItems.unitMoreA11y', { item: name }),
              }}
              onChange={next => {
                onUnits(next);
                // The stepper leaves with the last unit; keep focus on the row.
                if (next === 0) circleRef.current?.focus();
              }}
            />
          </div>
        ) : null}
      </div>
      {readOnly || !onEdit ? null : (
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('lineItems.editItem', { item: name })}
          className="mt-0.5 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <PencilIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

export default LineItemRow;
